// Helpers for the /sso screen: the portal's "Go to WMS" lands here with a one-time code in
// the URL fragment (never the query string, so it stays out of referrers, logs and analytics).

/** The code and optional return path carried in `#code=...&next=/app/...`. */
export const parseHandoffFragment = hash => {
  const raw = typeof hash === 'string' ? hash.replace(/^#/, '') : '';
  const params = new URLSearchParams(raw);
  return {
    code: params.get('code') || '',
    next: safeRelativePath(params.get('next')),
  };
};

/**
 * A "return to" path is only honoured when it is a same-origin relative path:
 * starts with a single `/`, no scheme, no `//host`, no backslash tricks.
 * Anything else falls back to the app's own landing page.
 */
export const safeRelativePath = value => {
  if (typeof value !== 'string') return null;
  const path = value.trim();
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\'))
    return null;
  // No backslash and no control character (tab, CR/LF, NUL, DEL): browsers strip
  // some of them from URLs, which can turn `/\t/evil.com` into `//evil.com`.
  // biome-ignore lint/suspicious/noControlCharactersInRegex: rejecting them is the point
  if (/[\\\x00-\x1f\x7f]/.test(path) || path.includes('://')) return null;
  return path;
};

export const HANDOFF_FAILED_MESSAGE =
  'Sign-in from the portal did not work. Please sign in with your warehouse account.';

/** What the login page should say after a failed handoff, from the API error when it is specific. */
export const handoffFailureMessage = error => {
  const code = error?.code;
  if (code === 'no_wms_account') {
    return 'There is no warehouse account for your portal email. Sign in with a warehouse account or contact support.';
  }
  if (code === 'wms_account_inactive' || code === 'account_locked') {
    return error.error || HANDOFF_FAILED_MESSAGE;
  }
  if (code === 'handoff_code_invalid') {
    return 'This sign-in link has expired. Go back to the portal and use "Go to WMS" again.';
  }
  return HANDOFF_FAILED_MESSAGE;
};

/**
 * A failed handoff is remembered for the tab so that a reload of the warehouse login page
 * (which loses router state) still shows the reason instead of bouncing back to the portal,
 * which could hand off again and loop. Cleared by the next successful handoff.
 */
export const HANDOFF_FAILED_KEY = 'wms.handoffFailed';

export const markHandoffFailed = storage => {
  try {
    storage?.setItem(HANDOFF_FAILED_KEY, '1');
  } catch {
    // Storage can be unavailable (private mode, blocked); the router state still carries the message.
  }
};

export const clearHandoffFailed = storage => {
  try {
    storage?.removeItem(HANDOFF_FAILED_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
};

export const handoffFailedRecently = storage => {
  try {
    return storage?.getItem(HANDOFF_FAILED_KEY) === '1';
  } catch {
    return false;
  }
};
